import React, { useCallback, useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Modal,
  Pagination,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  ArrowClockwise,
  CheckCircle,
  Envelope,
  ExclamationTriangle,
  Eye,
  Funnel,
  Search,
  Trash,
  XCircle,
} from "react-bootstrap-icons";
import axios from "axios";
import { toast } from "react-toastify";
import API_BASE_URL from "../../../config/api";

const STATUS_OPTIONS = [
  { value: "pending", label: "Pending", variant: "warning" },
  { value: "approved", label: "Approved", variant: "success" },
  { value: "rejected", label: "Rejected", variant: "danger" },
];

const ACTION_COPY = {
  approve: {
    label: "Approve",
    variant: "success",
    icon: <CheckCircle className="me-2" />,
    defaultReason: "Approved by admin",
  },
  reject: {
    label: "Reject",
    variant: "danger",
    icon: <XCircle className="me-2" />,
    defaultReason: "Request rejected",
  },
};

const getStatusConfig = (status) =>
  STATUS_OPTIONS.find((item) => item.value === status) || STATUS_OPTIONS[0];

const getItems = (payload) =>
  payload?.data?.requests ||
  payload?.data?.accountDeletionRequests ||
  payload?.data?.items ||
  payload?.requests ||
  payload?.accountDeletionRequests ||
  payload?.items ||
  payload?.data ||
  [];

const getTotalRecords = (payload, fallback) =>
  payload?.pagination?.totalRecords ||
  payload?.data?.pagination?.totalRecords ||
  payload?.totalRecords ||
  payload?.count ||
  fallback;

const getTotalPages = (payload, fallback, limit) =>
  payload?.pagination?.totalPages ||
  payload?.data?.pagination?.totalPages ||
  payload?.totalPages ||
  Math.max(1, Math.ceil(getTotalRecords(payload, fallback) / limit));

const formatDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const AccountDeletionRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("pending");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showView, setShowView] = useState(false);
  const [reviewAction, setReviewAction] = useState("");
  const [adminReason, setAdminReason] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const limit = 10;

  const token =
    localStorage.getItem("adminToken") || localStorage.getItem("token");

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${API_BASE_URL}/api/admin/adminUsers/account-deletion-requests`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            page: currentPage,
            limit,
            status: filterStatus || undefined,
            search: searchTerm || undefined,
          },
        }
      );

      const nextRequests = getItems(res.data);
      const normalizedRequests = Array.isArray(nextRequests) ? nextRequests : [];
      setRequests(normalizedRequests);
      setTotalRecords(getTotalRecords(res.data, normalizedRequests.length));
      setTotalPages(getTotalPages(res.data, normalizedRequests.length, limit));
    } catch (err) {
      console.error("Fetch account deletion requests error:", err);
      toast.error("Failed to fetch delete account requests");
    } finally {
      setLoading(false);
    }
  }, [currentPage, filterStatus, searchTerm, token]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const openReviewModal = (request, action) => {
    setSelectedRequest(request);
    setReviewAction(action);
    setAdminReason(ACTION_COPY[action].defaultReason);
  };

  const closeReviewModal = () => {
    setReviewAction("");
    setAdminReason("");
  };

  const reviewRequest = async () => {
    const requestId = selectedRequest?._id || selectedRequest?.id;
    if (!requestId) {
      toast.error("Missing request id");
      return;
    }

    try {
      setUpdatingId(requestId);
      await axios.put(
        `${API_BASE_URL}/api/admin/adminUsers/account-deletion-requests/${requestId}/review`,
        {
          action: reviewAction,
          adminReason: adminReason.trim(),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const nextStatus = reviewAction === "approve" ? "approved" : "rejected";
      toast.success(`Request ${nextStatus}`);
      setRequests((prev) =>
        prev.map((item) =>
          (item._id || item.id) === requestId
            ? { ...item, status: nextStatus, adminReason }
            : item
        )
      );
      setSelectedRequest((prev) =>
        prev && (prev._id || prev.id) === requestId
          ? { ...prev, status: nextStatus, adminReason }
          : prev
      );
      closeReviewModal();
      fetchRequests();
    } catch (err) {
      console.error("Review account deletion request error:", err);
      toast.error(err?.response?.data?.message || "Failed to review request");
    } finally {
      setUpdatingId("");
    }
  };

  const renderPagination = () => {
    const items = [];
    for (let page = 1; page <= totalPages; page += 1) {
      items.push(
        <Pagination.Item
          key={page}
          active={page === currentPage}
          onClick={() => setCurrentPage(page)}
        >
          {page}
        </Pagination.Item>
      );
    }
    return items;
  };

  const activeAction = reviewAction ? ACTION_COPY[reviewAction] : null;

  return (
    <div style={{ backgroundColor: "#fff", minHeight: "100vh" }}>
      <Card className="border-0 mb-4">
        <Card.Body className="p-0">
          <Row className="align-items-center g-3">
            <Col md={3}>
              <h4 className="fw-bold mb-0">Delete Account Requests</h4>
              <small className="text-muted">Review user deletion requests</small>
            </Col>

            <Col md={4}>
              <InputGroup>
                <InputGroup.Text>
                  <Search />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search by email or reason"
                  value={searchTerm}
                  onChange={(event) => {
                    setSearchTerm(event.target.value);
                    setCurrentPage(1);
                  }}
                />
              </InputGroup>
            </Col>

            <Col md={3}>
              <InputGroup>
                <InputGroup.Text>
                  <Funnel />
                </InputGroup.Text>
                <Form.Select
                  value={filterStatus}
                  onChange={(event) => {
                    setFilterStatus(event.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="">All Status</option>
                  {STATUS_OPTIONS.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </Form.Select>
              </InputGroup>
            </Col>

            <Col md={2} className="text-md-end">
              <Button variant="outline-primary" onClick={fetchRequests}>
                <ArrowClockwise className="me-2" />
                Refresh
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
              <p className="mt-2 text-muted">Loading requests...</p>
            </div>
          ) : requests.length === 0 ? (
            <div className="text-center py-5">
              <Trash size={42} className="text-muted mb-3" />
              <h5>No delete account requests found</h5>
              <p className="text-muted mb-0">New requests will show here.</p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead className="table-dark">
                    <tr>
                      <th>Email</th>
                      <th>Reason</th>
                      <th>Status</th>
                      <th>Requested</th>
                      <th>Admin Reason</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requests.map((request) => {
                      const requestId = request._id || request.id;
                      const statusConfig = getStatusConfig(request.status);
                      const isPending = (request.status || "pending") === "pending";

                      return (
                        <tr key={requestId}>
                          <td>
                            <Envelope className="me-1" />
                            {request.email || request.user?.email || "-"}
                          </td>
                          <td style={{ maxWidth: 330 }}>
                            <div className="text-truncate">
                              {request.reason || "-"}
                            </div>
                          </td>
                          <td>
                            <Badge bg={statusConfig.variant}>
                              {statusConfig.label}
                            </Badge>
                          </td>
                          <td>
                            <small>
                              {formatDate(request.createdAt || request.updatedAt)}
                            </small>
                          </td>
                          <td style={{ maxWidth: 240 }}>
                            <div className="text-truncate">
                              {request.adminReason || "-"}
                            </div>
                          </td>
                          <td>
                            <div className="d-flex flex-wrap gap-2 align-items-center">
                              <Button
                                size="sm"
                                variant="primary"
                                onClick={() => {
                                  setSelectedRequest(request);
                                  setShowView(true);
                                }}
                                title="View request"
                              >
                                <Eye />
                              </Button>
                              {isPending && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="success"
                                    disabled={updatingId === requestId}
                                    onClick={() => openReviewModal(request, "approve")}
                                  >
                                    <CheckCircle className="me-1" />
                                    Approve
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline-danger"
                                    disabled={updatingId === requestId}
                                    onClick={() => openReviewModal(request, "reject")}
                                  >
                                    <XCircle className="me-1" />
                                    Reject
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>

              <div className="p-3 d-flex justify-content-between align-items-center">
                <small className="text-muted">
                  Showing {(currentPage - 1) * limit + 1}-
                  {Math.min(currentPage * limit, totalRecords)} of {totalRecords}
                </small>
                {totalPages > 1 && (
                  <Pagination className="mb-0">
                    <Pagination.Prev
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                    />
                    {renderPagination()}
                    <Pagination.Next
                      disabled={currentPage === totalPages}
                      onClick={() =>
                        setCurrentPage((page) => Math.min(totalPages, page + 1))
                      }
                    />
                  </Pagination>
                )}
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      <Modal show={showView} onHide={() => setShowView(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Delete Account Request</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRequest && (
            <>
              <Row className="g-3 mb-4">
                <Col md={6}>
                  <strong>Email</strong>
                  <div>{selectedRequest.email || selectedRequest.user?.email || "-"}</div>
                </Col>
                <Col md={6}>
                  <strong>Status</strong>
                  <div className="mt-1">
                    <Badge bg={getStatusConfig(selectedRequest.status).variant}>
                      {getStatusConfig(selectedRequest.status).label}
                    </Badge>
                  </div>
                </Col>
                <Col md={6}>
                  <strong>Requested</strong>
                  <div>
                    {formatDate(selectedRequest.createdAt || selectedRequest.updatedAt)}
                  </div>
                </Col>
                <Col md={6}>
                  <strong>Admin Reason</strong>
                  <div>{selectedRequest.adminReason || "-"}</div>
                </Col>
              </Row>

              <strong>Reason</strong>
              <div
                className="border rounded bg-light p-3 mt-2"
                style={{ whiteSpace: "pre-wrap" }}
              >
                {selectedRequest.reason || "-"}
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>

      <Modal show={!!reviewAction} onHide={closeReviewModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{activeAction?.label} Request</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {reviewAction === "approve" && (
            <div className="d-flex gap-2 text-danger mb-3">
              <ExclamationTriangle className="mt-1 flex-shrink-0" />
              <span>
                Approving will delete the user and related platform traces.
              </span>
            </div>
          )}
          <Form.Group>
            <Form.Label>Admin Reason</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={adminReason}
              onChange={(event) => setAdminReason(event.target.value)}
              placeholder="Add a reason for this decision"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeReviewModal}>
            Cancel
          </Button>
          <Button
            variant={activeAction?.variant}
            onClick={reviewRequest}
            disabled={!adminReason.trim() || updatingId}
          >
            {updatingId ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Saving...
              </>
            ) : (
              <>
                {activeAction?.icon}
                {activeAction?.label}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AccountDeletionRequests;
